import {
  mapUserProfileDto,
  mapWalletEntityDto,
  type UserProfileDto,
  type WalletEntityDto,
} from '@/types';

import type {
  AvatarIntentRequest,
  AvatarIntentResult,
  MePatchRequest,
  MeResponse,
  MeService,
} from '../meService';
import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`MeService`(R48.x 對齊 BE handoff 2026-05-25)
 * -----------------------------------------------------------------------------
 * 2 endpoints,都不掛 L1.5(`/api/me` 在 `GROUP_HEADER_EXEMPT_PATTERNS`):
 *   - `GET /api/me`:回 `{ creative_uid, profile, current_group_id, wallet }`
 *   - `PATCH /api/me`:body `{ current_group_id }`,response 跟 GET 一樣 shape
 *
 * BE response envelope `{ result: { ...snake_case } }`;mapper 拆出 camelCase。
 */

interface MeResponseDto {
  creative_uid: string;
  profile: UserProfileDto;
  current_group_id: string | null;
  wallet: WalletEntityDto | null;
}

/** T2b — `POST /api/me/avatar/intent` 的 result DTO(snake_case)。 */
interface AvatarIntentResultDto {
  upload_id: string;
  upload_url: string;
  expires_datetime: string;
  max_size_bytes: number;
  allowed_mime_types: string[];
}

const PATH = '/api/me';
const AVATAR_INTENT_PATH = '/api/me/avatar/intent';
const AVATAR_FINALIZE_PATH = '/api/me/avatar/finalize';
const AVATAR_PATH = '/api/me/avatar';

function mapAvatarIntentResultDto(dto: AvatarIntentResultDto): AvatarIntentResult {
  return {
    uploadId: dto.upload_id,
    uploadUrl: dto.upload_url,
    expiresDatetime: dto.expires_datetime,
    maxSizeBytes: dto.max_size_bytes,
    allowedMimeTypes: dto.allowed_mime_types,
  };
}

function mapMeResponseDto(dto: MeResponseDto): MeResponse {
  // BE handoff §2:空字串視同 null(「空 → group picker」分支由 caller 端決定)
  const currentGroupId = dto.current_group_id ? dto.current_group_id : null;
  return {
    creativeUid: dto.creative_uid,
    profile: mapUserProfileDto(dto.profile),
    currentGroupId,
    // `/me` 的 wallet 殼無 group_id(docs/47 §1.A);用 current_group_id 當 fallback,
    // 否則 walletStore setter 的 `groupId === gid` guard 會 drop 掉 `/me` 寫入。
    wallet: dto.wallet ? mapWalletEntityDto(dto.wallet, currentGroupId ?? undefined) : null,
  };
}

function buildPatchBody(req: MePatchRequest): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  // 對齊 BE handoff:全 snake_case;只 append 有帶的欄位(undefined skip → BE 不改該欄)
  if (req.currentGroupId !== undefined) body.current_group_id = req.currentGroupId;
  // T2a(docs/50 / M-16)— profile 欄位
  if (req.displayName !== undefined) body.display_name = req.displayName;
  if (req.locale !== undefined) body.locale = req.locale;
  if (req.timezone !== undefined) body.timezone = req.timezone;
  return body;
}

export function createHttpMeService(config: HttpClientConfig): MeService {
  return {
    async get(): Promise<MeResponse> {
      const env = await httpRequestWithRefresh<MeResponseDto>(config, 'GET', PATH);
      return mapMeResponseDto(unwrapResult(env, 'me.get'));
    },

    async patch(payload: MePatchRequest): Promise<MeResponse> {
      const env = await httpRequestWithRefresh<MeResponseDto>(
        config,
        'PATCH',
        PATH,
        buildPatchBody(payload),
      );
      return mapMeResponseDto(unwrapResult(env, 'me.patch'));
    },

    async createAvatarIntent(req: AvatarIntentRequest): Promise<AvatarIntentResult> {
      const env = await httpRequestWithRefresh<AvatarIntentResultDto>(
        config,
        'POST',
        AVATAR_INTENT_PATH,
        { mime_type: req.mimeType, size_bytes: req.sizeBytes },
      );
      return mapAvatarIntentResultDto(unwrapResult(env, 'me.avatarIntent'));
    },

    async finalizeAvatar(uploadId: string): Promise<MeResponse> {
      const env = await httpRequestWithRefresh<MeResponseDto>(
        config,
        'POST',
        AVATAR_FINALIZE_PATH,
        { upload_id: uploadId },
      );
      return mapMeResponseDto(unwrapResult(env, 'me.avatarFinalize'));
    },

    async removeAvatar(): Promise<MeResponse> {
      const env = await httpRequestWithRefresh<MeResponseDto>(config, 'DELETE', AVATAR_PATH);
      return mapMeResponseDto(unwrapResult(env, 'me.avatarRemove'));
    },
  };
}
